import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  setSelectedRabbiId,
  setTradition,
  getParasha,
  getSelectedRabbiId,
} from '../utils/userSelections';
import { useTranslation } from 'react-i18next';
import i18n from '../../admin/i18n';
import BottomNav from '../components/BottomNav';
import TopBar from '../components/TopBar';
import kid1Image from '../assets/kid1.webp';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import { firebaseApp } from '../utils/firebaseConfig';
import { mapRabbiDoc } from '../utils/rabbiesFirestore';
import {
  trackPageView,
  trackClick,
  trackSelection,
  trackRabbiSelection,
  trackNavigation,
  trackTraditionSelection,
} from '../utils/analytics';

// Color constants (matching AppOnboardingPage theme)
const BLUE_ACCENT = '#3B7BF4';
const GOLD_BUTTON = '#F5C842';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

// Tradition filter options
const TRADITIONS = ['all', 'sfarad', 'ashkenaz', 'yamen', 'moroccan'] as const;
type TraditionFilter = (typeof TRADITIONS)[number];

// Types
export interface RabbiProfile {
  id: string;
  initials: string; // Circle avatar text
  nameEn: string;
  nameHe: string;
  titleHe?: string; // e.g., "מורה לקריאה בתורה"
  titleEn?: string; // e.g., "Rabbi"
  rating: number; // average rating
  reviews: number; // review count
  tradition: 'Sfarad' | 'Ashkenaz' | 'Yamen' | 'Jerusalem';
  priceMonthly: number; // USD per month
  sampleUrl?: string; // optional audio sample
  imageUrl?: string; // optional profile image
  experienceYears?: number;
  studentCount?: number;
  roles?: string[]; // e.g., ["חזן"]
  phone?: string; // optional phone number for calling
  email?: string; // optional email for contact / request parasha
  /** Parashiot this rabbi has recorded (English names). Used to show "with your parasha" section. */
  recordedParashiot?: string[];
  /** Unique key (e.g. for deep links). */
  key?: string;
}

// Avatar component with placeholder
const Avatar: React.FC<{ imageUrl?: string; alt: string }> = ({ imageUrl, alt }) => {
  const [imageError, setImageError] = useState(false);
  const showPlaceholder = !imageUrl || imageError;

  return (
    <div className="flex-shrink-0 w-32 h-32 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold overflow-hidden relative">
      {showPlaceholder ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-300">
          <svg
            className="w-16 h-16 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={alt}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      )}
    </div>
  );
};

const ChooseRabbiPage: React.FC = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(() => getSelectedRabbiId());
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [rabbis, setRabbis] = useState<RabbiProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noParashaPopupRabbiId, setNoParashaPopupRabbiId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Tradition filter state - initialize to "all" by default
  const [selectedTradition, setSelectedTraditionState] = useState<TraditionFilter>('all');

  // Handle tradition filter change
  const handleTraditionChange = (tradition: TraditionFilter) => {
    setSelectedTraditionState(tradition);
    trackSelection('tradition_filter', tradition);
    setTradition({ key: tradition, length: 'long' });
    trackTraditionSelection(tradition, 'long');
  };

  // Track page view
  useEffect(() => {
    trackPageView('ChooseRabbi');
  }, []);

  // Stop any playing sample when leaving the page
  useEffect(() => {
    return () => {
      const audio = document.getElementById('rabbi-audio') as HTMLAudioElement | null;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  // Fetch rabbis directly from Firestore
  useEffect(() => {
    const fetchRabbis = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get Firestore instance
        const db = getFirestore(firebaseApp);

        // Get reference to the 'rabbies' collection
        const rabbiesCollectionRef = collection(db, 'rabbies');

        // Get all documents in the collection
        const querySnapshot = await getDocs(rabbiesCollectionRef);

        // Map the fetched data to RabbiProfile format
        const mappedRabbis: RabbiProfile[] = [];

        querySnapshot.forEach((docSnap) => {
          const rabbi = docSnap.data();
          // Only show approved rabbis or rabbis with no status (backward compatibility)
          const status = rabbi.status as string | undefined;
          if (status != null && status !== 'approved') return;

          mappedRabbis.push(mapRabbiDoc(docSnap.id, rabbi));
        });

        setRabbis(mappedRabbis);
      } catch (err) {
        console.error('Error fetching rabbis:', err);
        setError('Failed to load rabbis. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    void fetchRabbis();
  }, []);

  const filtered = useMemo(() => {
    // Filter by tradition (if not "all")
    const traditionFilteredRabbis =
      selectedTradition === 'all'
        ? rabbis
        : rabbis.filter((r) => r.tradition.toLowerCase() === selectedTradition.toLowerCase());

    // Then filter by search query
    if (!query.trim()) return traditionFilteredRabbis;
    const lower = query.toLowerCase();
    return traditionFilteredRabbis.filter(
      (r) =>
        r.nameEn.toLowerCase().includes(lower) ||
        r.nameHe.includes(query) ||
        r.initials.toLowerCase().includes(lower) ||
        r.tradition.toLowerCase().includes(lower),
    );
  }, [query, rabbis, selectedTradition]);

  // User's selected parasha (english key for matching Firestore recordedParashiot)
  const currentParashaKey = getParasha()?.english ?? null;

  const allIncludeRabbiesList = useMemo(() => ['dvir_example_com'], []);
  // Partition: rabbis with user's parasha recorded vs without (only when user has a parasha)
  // When recordedParashiot is not array, null, or undefined → treat as rabbi has all parashot
  const { withParasha, withoutParasha } = useMemo(() => {
    if (!currentParashaKey) {
      return { withParasha: [] as RabbiProfile[], withoutParasha: filtered };
    }
    const hasParasha = (r: RabbiProfile) => {
      if (allIncludeRabbiesList.find((a) => a === r.key)) {
        return true;
      }
      const rp = r.recordedParashiot;
      if (rp == null || !Array.isArray(rp)) return true; // has all parashot
      return rp.includes(currentParashaKey);
    };
    const withP = filtered.filter(hasParasha);
    const withoutP = filtered.filter((r) => !hasParasha(r));
    return { withParasha: withP, withoutParasha: withoutP };
  }, [currentParashaKey, filtered, allIncludeRabbiesList]);

  const handleSample = useCallback(
    (rabbi: RabbiProfile) => {
      if (!rabbi.sampleUrl) return;
      // For now simple HTMLAudioElement usage; could be replaced by a player component
      if (playingId === rabbi.id) {
        // stop current
        const existing = document.getElementById('rabbi-audio') as HTMLAudioElement | null;
        existing?.pause();
        setPlayingId(null);
        return;
      }
      let audio = document.getElementById('rabbi-audio') as HTMLAudioElement | null;
      if (!audio) {
        audio = document.createElement('audio');
        audio.id = 'rabbi-audio';
        audio.style.display = 'none';
        document.body.appendChild(audio);
      }
      audio.src = rabbi.sampleUrl;
      audio.onended = () => setPlayingId(null);
      audio.play().catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Audio play failed', err);
        setPlayingId(null);
      });
      setPlayingId(rabbi.id);
    },
    [playingId],
  );

  return (
    <div
      className="flex flex-col flex-grow bg-white"
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <TopBar title={t('choose_rabbi_title')} onBack={() => navigate(-1)} />
      <div className="flex-grow overflow-y-auto p-4 pb-20">
        <div className="flex flex-col">
          <h1 className="text-2xl font-bold text-center mb-1" style={{ color: TEXT_DARK }}>
            {t('choose_rabbi_title')}
          </h1>

          <p className="text-center text-sm mb-4" style={{ color: TEXT_SECONDARY }}>
            {t('choose_rabbi_tagline')}
          </p>

          {/* Tradition Filter Buttons - RTL in Hebrew (All on right), LTR in English (All on left) */}
          <div className="mb-4">
            <div
              className="flex justify-between gap-1"
              style={{ direction: i18n.language === 'he' ? 'rtl' : 'ltr' }}
            >
              {TRADITIONS.map((tradKey) => {
                const isActive = tradKey === selectedTradition;
                return (
                  <button
                    key={tradKey}
                    type="button"
                    onClick={() => handleTraditionChange(tradKey)}
                    aria-pressed={isActive}
                    className="flex-1 transition-all duration-200"
                    style={{
                      padding: '8px 2px',
                      borderRadius: '12px',
                      border: isActive ? `2px solid ${BLUE_ACCENT}` : `2px solid ${BORDER_LIGHT}`,
                      background: isActive ? BLUE_ACCENT : 'white',
                      color: isActive ? '#FFFFFF' : TEXT_DARK,
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 12,
                      cursor: 'pointer',
                      boxShadow: isActive ? '0 4px 12px rgba(59, 123, 244, 0.3)' : 'none',
                      textAlign: 'center',
                    }}
                  >
                    {t(`tradition.${tradKey}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search - RTL for Hebrew so placeholder and input align right */}
          <div className="mb-3">
            <input
              type="text"
              placeholder={t('search_rabbis_placeholder') || 'Search rabbis...'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="kol-input w-full"
              style={{
                borderRadius: '18px',
                fontSize: 14,
                border: `1px solid ${BORDER_LIGHT}`,
                padding: '12px 16px',
                direction: i18n.language === 'he' ? 'rtl' : 'ltr',
                textAlign: i18n.language === 'he' ? 'right' : 'left',
              }}
              aria-label="Search rabbis"
            />
          </div>

          {loading && (
            <div className="text-center text-sm text-gray-500 py-6">Loading rabbis...</div>
          )}
          {error && <div className="text-center text-sm text-red-500 py-6">{error}</div>}
          {!loading && !error && (
            <>
              {currentParashaKey ? (
                <>
                  {/* Empty state: no rabbi has recorded the user's parasha */}
                  {withParasha.length === 0 && (
                    <section
                      className="mb-6 rounded-2xl p-5 border-2 border-dashed"
                      style={{
                        borderColor: BORDER_LIGHT,
                        background: '#F8F9FA',
                      }}
                      aria-label={String(t('choose_rabbi_no_parasha_empty_title'))}
                    >
                      <div
                        className="flex flex-col items-center gap-4"
                        style={{
                          direction: i18n.language === 'he' ? 'rtl' : 'ltr',
                          textAlign: i18n.language === 'he' ? 'right' : 'left',
                        }}
                      >
                        <img
                          src={kid1Image}
                          alt=""
                          className="object-contain"
                          style={{ maxHeight: 200, width: 'auto' }}
                        />
                        <div className="w-full max-w-md">
                          <h2 className="text-lg font-semibold mb-2" style={{ color: TEXT_DARK }}>
                            {t('choose_rabbi_no_parasha_empty_title')}
                          </h2>
                          <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                            {t('choose_rabbi_no_parasha_empty_message')}
                          </p>
                        </div>
                      </div>
                    </section>
                  )}
                  {/* Rabbis with your parasha (available) - white background */}
                  {withParasha.length > 0 && (
                    <section
                      className="mb-6"
                      aria-label={String(t('choose_rabbi_section_with_parasha'))}
                      style={{
                        direction: i18n.language === 'he' ? 'rtl' : 'ltr',
                        textAlign: i18n.language === 'he' ? 'right' : 'left',
                      }}
                    >
                      <h2 className="text-base font-semibold mb-3" style={{ color: TEXT_DARK }}>
                        {t('choose_rabbi_section_with_parasha')}
                      </h2>
                      <ul className="space-y-3 p-1" aria-label="Rabbis with your parasha">
                        {withParasha.map((rabbi) => renderRabbiCard(rabbi, true))}
                      </ul>
                    </section>
                  )}
                  {/* Rabbis without your parasha (available) - gray dimmed background */}
                  <section
                    aria-label={String(t('choose_rabbi_section_without_parasha'))}
                    style={{
                      direction: i18n.language === 'he' ? 'rtl' : 'ltr',
                      textAlign: i18n.language === 'he' ? 'right' : 'left',
                    }}
                  >
                    <h2 className="text-base font-semibold mb-3" style={{ color: '#dc2626' }}>
                      {t('choose_rabbi_section_without_parasha')}
                    </h2>
                    <ul className="space-y-3 p-1" aria-label="Rabbis without your parasha">
                      {withoutParasha.map((rabbi) => renderRabbiCard(rabbi, false))}
                    </ul>
                    {withoutParasha.length === 0 && withParasha.length === 0 && (
                      <p className="text-center text-sm text-gray-500 py-4">
                        {t('choose_rabbi_no_match')}
                      </p>
                    )}
                  </section>
                </>
              ) : (
                <ul
                  className="space-y-3 p-1"
                  aria-label="Rabbi list"
                  style={{
                    direction: i18n.language === 'he' ? 'rtl' : 'ltr',
                    textAlign: i18n.language === 'he' ? 'right' : 'left',
                  }}
                >
                  {filtered.map((rabbi) => renderRabbiCard(rabbi, true))}
                  {filtered.length === 0 && (
                    <li className="text-center text-sm text-gray-500 py-6">
                      {t('choose_rabbi_no_match')}
                    </li>
                  )}
                </ul>
              )}
            </>
          )}
        </div>
      </div>

      <div
        style={{
          flexShrink: 0,
          background: 'white',
          padding: '16px 20px',
          borderTop: `1px solid ${BORDER_LIGHT}`,
          boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.06)',
        }}
      >
        <button
          type="button"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            const rabbi = rabbis.find((r) => r.id === selectedId);
            console.log('Selected rabbi', rabbi);
            if (rabbi) {
              trackClick('Select Rabbi', 'ChooseRabbi');
              trackRabbiSelection(
                rabbi.id,
                i18n.language === 'he' ? rabbi.nameHe : rabbi.nameEn,
                rabbi.tradition,
              );
              // Persist only the id; rabbi details are always resolved from Firestore
              setSelectedRabbiId(rabbi.id);
              // Set tradition based on rabbi's tradition
              const rabbiTradition = rabbi.tradition.toLowerCase();
              setTradition({ key: rabbiTradition, length: 'long' });
              trackTraditionSelection(rabbiTradition, 'long');
            }
            // If navigated from a specific page (e.g. PracticePage), go back there
            const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
            if (returnTo) {
              trackNavigation('ChooseRabbi', returnTo, 'button');
              navigate(returnTo);
              return;
            }
            // Check if parasha was selected (via birthdate), if not go to ChooseParshaPage
            const hasParasha = getParasha();
            if (hasParasha) {
              trackNavigation('ChooseRabbi', 'Practice', 'button');
              navigate('/user-portal');
            } else {
              trackNavigation('ChooseRabbi', 'ChooseParasha', 'button');
              navigate('/choose-parasha');
            }
          }}
          style={{
            width: '100%',
            borderRadius: '28px',
            padding: '16px 0',
            fontWeight: 600,
            fontSize: '18px',
            transition: 'all 0.2s ease',
            border: 'none',
            cursor: selectedId ? 'pointer' : 'not-allowed',
            background: selectedId ? GOLD_BUTTON : BORDER_LIGHT,
            color: selectedId ? TEXT_DARK : TEXT_SECONDARY,
            boxShadow: selectedId ? '0 4px 16px rgba(245, 200, 66, 0.4)' : 'none',
          }}
        >
          {t('select_rabbi_button')}
        </button>
      </div>
      <BottomNav />

      {/* No-parasha popup */}
      {noParashaPopupRabbiId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
          }}
          onClick={() => setNoParashaPopupRabbiId(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '28px 24px',
              maxWidth: '360px',
              width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>

            <h2 style={{ fontWeight: 700, fontSize: 18, color: TEXT_DARK, marginBottom: 10 }}>
              {t('no_parasha_popup_title')}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
              {/* Option 1 — choose another rabbi */}
              <button
                type="button"
                onClick={() => setNoParashaPopupRabbiId(null)}
                style={{
                  width: '100%',
                  padding: '14px 0',
                  borderRadius: '28px',
                  border: `2px solid ${BLUE_ACCENT}`,
                  background: 'white',
                  color: BLUE_ACCENT,
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: 'pointer',
                }}
              >
                {t('no_parasha_popup_choose_another')}
              </button>

              {/* Option 2 — continue anyway */}
              <button
                type="button"
                onClick={() => {
                  const rabbiId = noParashaPopupRabbiId;
                  setNoParashaPopupRabbiId(null);
                  trackSelection('rabbi_preview', rabbiId, { no_parasha: true });
                  setSelectedId(rabbiId);
                }}
                style={{
                  width: '100%',
                  padding: '14px 0',
                  borderRadius: '28px',
                  border: 'none',
                  background: GOLD_BUTTON,
                  color: TEXT_DARK,
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(245, 200, 66, 0.4)',
                }}
              >
                {t('no_parasha_popup_continue_anyway')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderRabbiCard(rabbi: RabbiProfile, hasUserParasha: boolean) {
    const selected = rabbi.id === selectedId;
    const playing = rabbi.id === playingId;
    const cardBg = hasUserParasha ? 'white' : '#e5e7eb';
    const rabbiDisplayName = i18n.language === 'he' ? rabbi.nameHe : rabbi.nameEn;
    return (
      <li key={rabbi.id}>
        <div
          onClick={() => {
            if (
              !hasUserParasha &&
              currentParashaKey &&
              withoutParasha.find((item) => item.key === rabbi.key)
            ) {
              setNoParashaPopupRabbiId(rabbi.id);
              return;
            }
            trackSelection('rabbi_preview', rabbi.id, {
              name: rabbiDisplayName,
            });
            setSelectedId(rabbi.id);
          }}
          style={{
            width: '100%',
            borderRadius: '20px',
            background: cardBg,
            boxShadow: selected
              ? `0 0 0 2px ${BLUE_ACCENT}, 0 4px 16px rgba(59, 123, 244, 0.2)`
              : '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: selected ? `1px solid ${BLUE_ACCENT}` : `1px solid ${BORDER_LIGHT}`,
            padding: 16,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <div className="flex items-start justify-between gap-4">
            {/* Details */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                textAlign: i18n.language === 'he' ? 'right' : 'left',
              }}
            >
              {/* Name */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    fontWeight: 700,
                    color: TEXT_DARK,
                    fontSize: 20,
                    lineHeight: 1.3,
                  }}
                >
                  {rabbiDisplayName}
                </span>
                {(i18n.language === 'he' ? rabbi.titleHe : rabbi.titleEn) && (
                  <span
                    style={{
                      fontSize: 13,
                      color: TEXT_SECONDARY,
                      marginTop: 2,
                    }}
                  >
                    {i18n.language === 'he' ? rabbi.titleHe : rabbi.titleEn}
                  </span>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  flexDirection: i18n.language === 'he' ? 'row-reverse' : 'row',
                  direction: i18n.language === 'he' ? 'rtl' : 'ltr',
                  justifyContent: i18n.language === 'he' ? 'flex-end' : 'flex-start',
                }}
              >
                {rabbi.roles &&
                  rabbi.roles.map((role, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '4px 12px',
                        borderRadius: '9999px',
                        background: 'rgba(59, 123, 244, 0.1)',
                        color: BLUE_ACCENT,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      {role}
                    </span>
                  ))}
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '9999px',
                    background: 'rgba(26, 26, 46, 0.08)',
                    color: TEXT_DARK,
                    fontSize: 16,
                    fontWeight: 500,
                    direction: i18n.language === 'he' ? 'rtl' : 'ltr',
                    textAlign: i18n.language === 'he' ? 'right' : 'left',
                  }}
                >
                  {t('tradition.' + rabbi.tradition.toLowerCase())}
                </span>
              </div>
            </div>
            {/* Avatar */}
            <Avatar imageUrl={rabbi.imageUrl} alt={rabbiDisplayName} />
          </div>

          {/* Sample button - moved to bottom right */}
          {rabbi.sampleUrl && (
            <div
              style={{
                marginTop: 12,
                display: 'flex',
                justifyContent: i18n.language === 'he' ? 'flex-end' : 'flex-start',
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  trackClick('Play Sample', 'ChooseRabbi', {
                    rabbi_id: rabbi.id,
                  });
                  handleSample(rabbi);
                }}
                style={{
                  border: playing ? 'none' : `1.5px solid ${BLUE_ACCENT}`,
                  borderRadius: '9999px',
                  padding: '8px 16px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: playing ? BLUE_ACCENT : 'white',
                  color: playing ? 'white' : BLUE_ACCENT,
                }}
                aria-label={`Play sample of ${rabbiDisplayName}`}
              >
                {playing ? '⏸ Stop' : '▶ Sample'}
              </button>
            </div>
          )}
        </div>
      </li>
    );
  }
};

export default ChooseRabbiPage;
